import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ShieldAlert, ShieldCheck } from 'lucide-react';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';
import { Card } from '@siliconx/shared/ui/Card';
import { Button } from '@siliconx/shared/ui/Button';
import { StatusPill } from '@siliconx/shared/ui/StatusPill';
import { InsightBanner } from '@siliconx/shared/ui/InsightBanner';
import { qk } from '../../lib/queryKeys';
import {
  approveBenchPairing,
  denyBenchPairing,
  lookupBenchPairing,
  normalizeUserCodeInput,
} from '../../lib/bench';

/**
 * Bench pairing approval. The bench agent shows a QR code (this page with
 * ?code=XXXX-XXXX) and the same code as text. The technician signs in (the
 * root beforeLoad stashes this URL and restores it after Cognito) and taps
 * Approve once. Everything in the device card was reported by the device
 * and is unverified, so it is rendered as plain text and labelled as such.
 * The one-tap approval with device details and source IP is the defense
 * against a QR link for someone else's device (RFC 8628 section 5.4).
 */
export const Route = createFileRoute('/bench/pair')({
  validateSearch: (search: Record<string, unknown>): { code?: string } =>
    typeof search.code === 'string' ? { code: search.code } : {},
  component: BenchPairRoute,
});

function BenchPairRoute() {
  const { code } = Route.useSearch();
  return <BenchPairView initialCode={code ?? null} />;
}

function errorStatus(err: unknown): number | undefined {
  return (err as { status?: number } | null)?.status;
}

function lookupErrorMessage(err: unknown): string {
  if (errorStatus(err) === 429) return 'Too many attempts. Wait 10 minutes, then try again.';
  return 'This code is not valid or has expired. Check the code on the bench screen.';
}

export function BenchPairView({ initialCode }: { initialCode: string | null }) {
  const navigate = useNavigate();
  const [code, setCode] = useState<string | null>(initialCode ? normalizeUserCodeInput(initialCode) : null);
  const [input, setInput] = useState(initialCode ?? '');
  const [inputError, setInputError] = useState<string | null>(
    initialCode && !normalizeUserCodeInput(initialCode) ? 'Enter the 8 letters shown on the bench screen.' : null,
  );
  const [denied, setDenied] = useState(false);

  const lookup = useQuery({
    queryKey: qk.bench.pairing(code ?? ''),
    queryFn: () => lookupBenchPairing(code!),
    enabled: code !== null && !denied,
    retry: false,
    staleTime: Infinity,
  });

  const approve = useMutation({
    mutationFn: () => approveBenchPairing(code!),
    onSuccess: ({ runId }) => navigate({ to: '/bench/runs/$runId', params: { runId } }),
  });

  const deny = useMutation({
    mutationFn: () => denyBenchPairing(code!),
    onSuccess: () => setDenied(true),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    const normalized = normalizeUserCodeInput(input);
    if (!normalized) {
      setInputError('Enter the 8 letters shown on the bench screen.');
      return;
    }
    setInputError(null);
    setCode(normalized);
  }

  const showForm = code === null || lookup.isError;
  const busy = approve.isPending || deny.isPending;
  const d = lookup.data;

  return (
    <div className="mx-auto max-w-xl space-y-6 p-4">
      <PageHeader
        title="Pair a bench device"
        subtitle="Approve only the device that is on the bench in front of you."
      />

      {denied && (
        <InsightBanner tone="neutral" lead="Pairing denied.">
          The bench device cannot send results to your account. It shows a new code if someone pairs it again.
        </InsightBanner>
      )}

      {lookup.isError && !denied && (
        <InsightBanner tone="amber" lead={lookupErrorMessage(lookup.error)} />
      )}

      {showForm && !denied && (
        <Card>
          <form onSubmit={submit} className="space-y-3">
            <label htmlFor="bench-pair-code" className="block text-sm font-medium">
              Pairing code
            </label>
            <input
              id="bench-pair-code"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              placeholder="XXXX-XXXX"
              className="w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-lg tracking-widest"
            />
            {inputError && <p className="text-sm text-status-red">{inputError}</p>}
            <Button type="submit">Continue</Button>
          </form>
        </Card>
      )}

      {lookup.isPending && code !== null && !denied && <p className="text-sm text-muted-foreground">Looking up the device...</p>}

      {d && !denied && !lookup.isError && (
        <Card className="space-y-4">
          <div className="flex items-center justify-between gap-2">
            <span className="font-mono text-lg tracking-widest">{code}</span>
            <StatusPill tone="amber" title="Every value below was sent by the device itself.">
              Reported by device, unverified
            </StatusPill>
          </div>

          <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-2 text-sm">
            <dt className="text-muted-foreground">Chassis serial</dt>
            <dd className="font-mono break-all">{d.deviceSummary.chassisSerial ?? 'Not reported'}</dd>
            <dt className="text-muted-foreground">Model</dt>
            <dd className="break-all">{d.deviceSummary.productName ?? 'Not reported'}</dd>
            <dt className="text-muted-foreground">Vendor</dt>
            <dd className="break-all">{d.deviceSummary.sysVendor ?? 'Not reported'}</dd>
            <dt className="text-muted-foreground">Board serial</dt>
            <dd className="font-mono break-all">{d.deviceSummary.boardSerial ?? 'Not reported'}</dd>
            <dt className="text-muted-foreground">MAC address</dt>
            <dd className="font-mono">{d.deviceSummary.primaryMac ?? 'Not reported'}</dd>
            <dt className="text-muted-foreground">Devices found</dt>
            <dd>{d.deviceSummary.deviceCount ?? 'Not reported'}</dd>
            <dt className="text-muted-foreground">Source IP</dt>
            <dd className="font-mono">{d.sourceIp}</dd>
          </dl>

          {d.siteMatch ? (
            <p className="flex items-center gap-2 text-sm text-status-green">
              <ShieldCheck className="h-4 w-4" aria-hidden />
              This IP is inside a registered bench site of your organization.
            </p>
          ) : (
            <p className="flex items-center gap-2 text-sm text-status-amber">
              <ShieldAlert className="h-4 w-4" aria-hidden />
              This IP is not a registered bench site. Results will be marked self-reported.
            </p>
          )}

          {(approve.isError || deny.isError) && (
            <p className="text-sm text-status-red">
              {lookupErrorMessage(approve.error ?? deny.error)}
            </p>
          )}

          <div className="flex gap-3">
            <Button onClick={() => approve.mutate()} disabled={busy}>
              Approve
            </Button>
            <Button variant="secondary" onClick={() => deny.mutate()} disabled={busy}>
              Deny
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
